// Stable state-key derivation for MAP tasks. Single source of truth for
// the `<widgetId>:<???>` shape used in RoomState.taskStatus and the
// renderer.
//
// Migration story (in flight): old rooms key tasks positionally
// (`<widgetId>:<phaseIdx>:<taskIdx>`). New rooms key by stable taskId
// (`<widgetId>:<taskId>`). The renderer prefers taskId when present and
// falls back to positional, so both shapes coexist during the rollout
// without losing state.
//
// scripts/migrate-map-task-ids.mjs rewrites old positional state files
// onto the stable form one room at a time. After a
// room is migrated, the positional fallback is unreachable for its tasks.

export interface MapTaskShape {
    taskId?: string;
    title?: string;
}

export interface MapTaskKeyArgs {
    widgetId: string;
    task: MapTaskShape;
    phaseIdx: number;
    taskIdx: number;
}

export function mapTaskKey({ widgetId, task, phaseIdx, taskIdx }: MapTaskKeyArgs): string {
    if (task.taskId) return `${widgetId}:${task.taskId}`;
    return `${widgetId}:${phaseIdx}:${taskIdx}`;
}

export function isStableMapTaskKey(key: string): boolean {
    // Stable keys are `<widgetId>:<kebab-id>`; positional keys are
    // `<widgetId>:<int>:<int>`. The second segment of a positional key is
    // a plain integer, so anything else marks a stable key.
    const parts = key.split(":");
    if (parts.length !== 3 && parts.length !== 2) return false;
    if (parts.length === 2) return true; // already collapsed to widgetId:taskId
    return !/^\d+$/.test(parts[1]);
}
